import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, onValue } from 'firebase/database';
import { initializeApp, getApps } from 'firebase/app';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage'; // Add Storage imports
import { getMessaging, getToken } from 'firebase/messaging'; // Import Firebase Messaging
import { useTranslation } from 'react-i18next'; // <-- IMPORT useTranslation
import * as LucideIcons from 'lucide-react'; // <-- IMPORT LUCIDE ICONS
import { firebaseConfig } from './firebase-config';

// Safely initialize Firebase, preventing duplicate app instances
let app;
if (!getApps().length) {
    app = initializeApp(firebaseConfig);
} else {
    app = getApps()[0]; // Use the existing app instance
}

// Initialize Firebase services
const auth = getAuth(app);
const db = getFirestore(app);
const rtdb = getDatabase(app);
const appFunctions = getFunctions(app, 'us-central1');
const storage = getStorage(app); // Initialize Storage
const messaging = getMessaging(app); // Initialize Firebase Messaging
const appId = "whispers-of-harmony"; // Define appId

// Create the context
export const AppContext = createContext({});
export const useAppContext = () => useContext(AppContext);

// Create the provider component
export const AppProvider = ({ children }) => {
    const { t } = useTranslation(); // <-- CALL useTranslation
    const [message, setMessage] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [onlineStatus, setOnlineStatus] = useState({});

    // Effect to listen for real-time presence updates
    useEffect(() => {
        const statusRef = ref(rtdb, 'status/');
        const unsubscribe = onValue(statusRef, (snapshot) => {
            setOnlineStatus(snapshot.val());
        });

        // Cleanup listener on component unmount
        return () => unsubscribe();
    }, []);

    const showToast = useCallback((text, type = 'info') => {
        console.log(`Toast (${type}):`, text);
        setMessage({ text, type });
        // Automatically dismiss the toast after 3 seconds
        setTimeout(() => setMessage(null), 3000);
    }, []);

    const showConfirmation = useCallback(({ message, onConfirm }) => {
        setConfirmation({ message, onConfirm });
    }, []);

    const closeConfirmation = () => {
        setConfirmation(null);
    };

    const uploadFile = useCallback(async (file, filePath, onProgress) => {
        const fileRef = storageRef(storage, filePath);
        const uploadTask = uploadBytesResumable(fileRef, file);

        return new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
                (snapshot) => {
                    const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                    onProgress(progress);
                },
                (error) => {
                    console.error("Upload failed:", error);
                    showToast(`Upload failed: ${error.message}`, 'error');
                    reject(error);
                },
                async () => {
                    const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                    resolve(downloadURL);
                }
            );
        });
    }, [storage, showToast]); // Add storage and showToast to dependencies

    const updateUserProfile = useCallback(async (profileId, dataToUpdate) => {
        try {
            await db.collection(`artifacts/${appId}/public/data/user_profiles`).doc(profileId).update(dataToUpdate);
            showToast('Profile updated successfully!', 'success');
        } catch (error) {
            console.error("Error updating user profile:", error);
            showToast(`Failed to update profile: ${error.message}`, 'error');
            throw error; // Re-throw to allow calling component to handle
        }
    }, [db, showToast]);

    const [userConnections, setUserConnections] = useState([]);

    useEffect(() => {
        if (auth.currentUser) {
            const connectionsRef = collection(db, `artifacts/${appId}/users/${auth.currentUser.uid}/connections`);
            const unsubscribe = onSnapshot(connectionsRef, (snapshot) => {
                const connections = snapshot.docs.map(doc => doc.data());
                setUserConnections(connections);
            });
            return () => unsubscribe();
        }
    }, [auth.currentUser]);

    // The value provided to all consuming components
    const value = {
        auth,
        db,
        rtdb,
        appFunctions: appFunctions,
        showToast,
        setMessage: showToast, // Alias for backward compatibility if needed
        showConfirmation,
        uploadFile,
        updateUserProfile, // Provide updateUserProfile via context
        messaging, // Provide messaging via context
        userConnections,
        setUserConnections,
        onlineStatus, // Provide online status
        appId, // EXPORT THE APPID
        t, // <-- ADD t TO THE CONTEXT VALUE
        LucideIcons, // <-- ADD LUCIDE ICONS TO THE CONTEXT VALUE
    };

    return (
        <AppContext.Provider value={value}>
            {children}
            
            {/* Render Toast Notifications */}
            {message && (
                <div className={`notification-toast visible ${message.type === 'error' ? 'border-red-500' : 'border-blue-500'}`}>
                    <div className="harmonic-glow"></div>
                    {message.text}
                </div>
            )}

            {/* Render Confirmation Dialogs */}
            {confirmation && (
                <div className="modal-overlay">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-sm w-full text-center">
                        <p className="text-lg font-semibold mb-4 text-blue-200">{confirmation.message}</p>
                        <div className="flex justify-center space-x-4">
                            <button onClick={closeConfirmation} className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">Cancel</button>
                            <button onClick={() => { confirmation.onConfirm(); closeConfirmation(); }} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </AppContext.Provider>
    );
};
